/**
 * (c) 2021, Nous Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { Box, Flex } from "@chakra-ui/layout";
import { useMediaQuery, Spinner } from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { RiExternalLinkLine } from "react-icons/ri";
import { useIntl } from "react-intl";
import {
  hideSidebarMediaQuery,
  sidebarToWidthRatio,
  simulatorToWidthRatio,
  widthToHideSidebar,
} from "../common/screenWidthUtils";
import {
  SplitView,
  SplitViewDivider,
  SplitViewRemainder,
  SplitViewSized,
} from "../common/SplitView";
import { SizedMode } from "../common/SplitView/SplitView";
import { zIndexAboveDialogs } from "../common/zIndex";
import { useDeployment } from "../deployment";
import { ConnectionStatus } from "../device/device";
import { useConnectionStatus } from "../device/device-hooks";
import EditorArea from "../editor/EditorArea";
import { MAIN_FILE } from "../fs/fs";
import { useProject } from "../project/project-hooks";
import { useProjectActions } from "../project/project-hooks";
import ProjectActionBar from "../project/ProjectActionBar";
import SerialArea from "../serial/SerialArea";
import { supportedLanguages, useSettings } from "../settings/settings";
import Simulator from "../simulator/Simulator";
import Overlay from "./connect-dialogs/Overlay";
import SideBar from "./SideBar";
import { useSelection } from "./use-selection";
import MenuBar from '../menu-bar/menu-bar';
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/modalStore'
import { setIsIframe, setUserLoginState } from '../store/user-state'
import {
  useIframeMessage,
  IframeMessage
} from './iframe-hooks'
import MonitorDrawer from '../monitorDrawer/index';
import './index.css'
// 弹框提示连接小车
import ConnectionBulletbox from '../containers/connection-bulletbox.jsx';
import ConnectedStep from '../connectNous/connected-step.jsx';

const minimums: [number, number] = [380, 580];
const simulatorMinimums: [number, number] = [275, 0];

/**
 * The main app layout with resizable panels.
 */
const Workbench = (fs) => {
  const dispatch = useDispatch();
  const [selection, setSelection] = useSelection();
  const intl = useIntl();
  const { files } = useProject();
  const actions = useProjectActions();
  const setSelectedFile = useCallback(
    (file: string) => {
      setSelection({ file, location: { line: undefined } });
    },
    [setSelection]
  );
  const [settings, setSettings] = useSettings();
  const syncLanguageFromUrl = useCallback(() => {
    const query = new URLSearchParams(window.location.search)
    const langParam = query.get('lang')
    if (!langParam) return
    const supportIds = supportedLanguages.map(item => item.id);
    //支持传入对应supportedLanguages定义的id
    const isSupported = supportIds.includes(langParam) || langParam === 'zh';
    const languageId = isSupported ? 'zh-cn' : 'en';
    if (settings.languageId !== languageId) {
      setSettings({
        ...settings,
        languageId,
      });
    }
  }, [settings.languageId, setSettings])
  useEffect(() => {
    syncLanguageFromUrl();
  }, [syncLanguageFromUrl]);


  const handleParentMessage = useCallback((message: IframeMessage<{ name?: string; buffer?: ArrayBuffer }>) => {
    if (message.type === 'receive-works-py') {
      const { name = 'main.py', buffer } = message.payload ?? {};
      if (!buffer) return;
      const file = new File([buffer], name, { type: 'text/plain' });
      actions.load([file]);
    }
  }, [actions]);

  useIframeMessage(handleParentMessage)

  useEffect(() => {
    const getUrlParams = (): Record<string, string> => {
      const params: Record<string, string> = {};
      const search = window.location.search.substring(1);
      const parts = search.split('&');
      for (let i = 0; i < parts.length; i++) {
        const pair = parts[i].split('=');
        if (pair.length === 2) {
          params[pair[0]] = decodeURIComponent(pair[1]);
        }
      }
      return params;
    };
    const { ch, token } = getUrlParams();

    if (ch === 'aiedu') {
      dispatch(setIsIframe(true));
    }

    const normalizedToken = String(token ?? '').trim().toLowerCase();
    const invalidToken = ['', 'null', 'undefined', "''", '""'].includes(normalizedToken)
    if (!invalidToken) {
      localStorage.setItem('token', token);
      dispatch(setUserLoginState(token));
    }
  }, [])


  useEffect(() => {
    // No file yet or selected file deleted? Default it.
    if (
      (!selection || !files.find((x) => x.name === selection.file)) &&
      files.length > 0
    ) {
      const defaultFile = files.find((x) => x.name === MAIN_FILE) || files[0];
      setSelectedFile(defaultFile.name);
    }
  }, [selection, setSelectedFile, files]);

  const fileVersion = files.find((f) => f.name === selection.file)?.version;

  const [sidebarShown, setSidebarShown] = useState<boolean>(
    () => window.innerWidth > widthToHideSidebar
  );
  const [simulatorShown, setSimulatorShown] = useState<boolean>(true);
  const simulatorButtonRef = useRef<HTMLButtonElement>(null);
  const [tabIndex, setTabIndex] = useState<number>(() =>
    window.innerWidth > widthToHideSidebar ? 0 : -1
  );
  const [simFocus, setSimFocus] = useState<boolean>(true);

  // Sidebar/simulator space management:
  const handleSidebarCollapse = useCallback(() => {
    setTabIndex(-1);
    setSidebarShown(false);
  }, []);
  const handleSidebarExpand = useCallback(() => {
    setSidebarShown(true);
    // If there's not room for the simulator then hide it.
    if (window.innerWidth <= widthToHideSidebar) {
      setSimFocus(false);
      setSimulatorShown(false);
    }
  }, []);
  const handleSimulatorHide = useCallback(() => {
    setSimFocus(true);
    setSimulatorShown(false);
  }, []);
  const handleSimulatorExpand = useCallback(() => {
    setSimulatorShown(true);
    // If there's not room for the sidebar then hide it.
    if (window.innerWidth <= widthToHideSidebar) {
      handleSidebarCollapse();
    }
  }, [handleSidebarCollapse]);
  const [hideSideBarMediaQueryValue] = useMediaQuery(hideSidebarMediaQuery);
  // 点击图标跳转
  const handleClickLogo = useCallback(
    () => {
      // const mobileOS = getMobileOS();
        // if (mobileOS == 'iOS') {
        //     window.webkit.messageHandlers.navigatorSend.postMessage('back');
        // } else if (mobileOS == 'Android') {
        //     window.matatalab.navigatorSend('back');
        // }
    },[]
  );
  useEffect(() => {
    if (hideSideBarMediaQueryValue) {
      handleSidebarCollapse();
    }
  }, [hideSideBarMediaQueryValue, handleSidebarCollapse]);

  const editor = (
    <Box height="100%" as="section">
      {selection && fileVersion !== undefined && (
        <EditorArea
          key={selection.file + "/" + fileVersion}
          selection={selection}
          onSelectedFileChanged={setSelectedFile}
          onSimulatorExpand={handleSimulatorExpand}
          simulatorShown={simulatorShown}
          ref={simulatorButtonRef}
        />
      )}
    </Box>
  );
  const inIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };
  const deployment = useDeployment();
  const Compliance = deployment.Compliance ?? (() => null);
  const openLoadingModalState = useSelector((state: RootState) => state.modalsStore.openLoadingModalState);
  const openconnectModalState = useSelector((state: RootState) => state.modalsStore.openconnectModalState);
  const openDisconnectModalState = useSelector((state: RootState) => state.modalsStore.openDisconnectModalState);
  return (
    <div>
      {/* loging */}
        <Flex className="WorkbenchContainer" flexDir="column">
          {/* 头部页签 */}
          <MenuBar 
            handleClickLogo={handleClickLogo}
            fs={fs}
          />
          {/* 侧边栏 */}
          <MonitorDrawer />
          {!inIframe() && (
            <Compliance
              zIndex={zIndexAboveDialogs}
              externalLinkIcon={RiExternalLinkLine}
            />
          )}
          <Flex className="Workbench">
            <SplitView
              direction="row"
              width="100%"
              minimums={minimums}
              initialSize={Math.min(
                700,
                Math.max(
                  minimums[0],
                  Math.floor(window.innerWidth * sidebarToWidthRatio)
                )
              )}
              compactSize={86}
              mode={sidebarShown ? "open" : "compact"}
            >
              <SplitViewSized>
                <SideBar
                  as="section"
                  aria-label={intl.formatMessage({ id: "sidebar" })}
                  selectedFile={selection.file}
                  onSelectedFileChanged={setSelectedFile}
                  flex="1 1 100%"
                  shown={sidebarShown}
                  tabIndex={tabIndex}
                  onTabIndexChange={setTabIndex}
                  onSidebarCollapse={handleSidebarCollapse}
                  onSidebarExpand={handleSidebarExpand}
                />
              </SplitViewSized>
              <SplitViewDivider />
              <SplitViewRemainder>
                <EditorWithSimulator
                  editor={editor}
                  onSimulatorHide={handleSimulatorHide}
                  simulatorShown={simulatorShown}
                  showSimulatorButtonRef={simulatorButtonRef}
                  simFocus={simFocus}
                  fs={fs}
                />
              </SplitViewRemainder>
            </SplitView>
          </Flex>
          <Overlay />
        </Flex>
        {openLoadingModalState ? 
          <div className="pythonSpinnerBack">
            <Spinner
              thickness='4px'
              speed='0.65s'
              emptyColor='gray.200'
              color='blue.500'
              size='xl'
              className="pythonSpinner"
            />
          </div> : null
        }
        {/* 连接小车弹框 */}
        {openconnectModalState ? 
          <div className="pythonSpinnerBack">
            <ConnectionBulletbox />
          </div> : null
        }
        {/* 断开小车弹框 */}
        {openDisconnectModalState ? 
          <div className="pythonSpinnerBack">
            <ConnectedStep />
          </div> : null
        }
    </div>
  );
};

interface EditorProps {
  editor: ReactNode;
  fs: ReactNode
}

interface EditorWithSimulatorProps extends EditorProps {
  simulatorShown: boolean;
  showSimulatorButtonRef: React.RefObject<HTMLButtonElement>;
  onSimulatorHide: () => void;
  simFocus: boolean;
}

const EditorWithSimulator = ({
  editor,
  fs,
  simulatorShown,
  showSimulatorButtonRef,
  onSimulatorHide,
  simFocus
}: EditorWithSimulatorProps) => {
  return (
    <SplitView
      direction="row"
      minimums={simulatorMinimums}
      height="100%"
      mode={simulatorShown ? "open" : "collapsed"}
      initialSize={Math.min(
        350,
        Math.max(
          simulatorMinimums[0],
          Math.floor(window.innerWidth * simulatorToWidthRatio)
        )
      )}
    >
       <Editor editor={editor} fs={fs} />
      {/* <SplitViewRemainder>
       
      </SplitViewRemainder> */}
      <></>
      <></>
      {/* <SplitViewDivider showBoxShadow={true} /> */}
      {/* <SplitViewSized>
        <Simulator
          shown={simulatorShown}
          onSimulatorHide={onSimulatorHide}
          showSimulatorButtonRef={showSimulatorButtonRef}
          minWidth={simulatorMinimums[0]}
          simFocus={simFocus}
        />
      </SplitViewSized> */}
    </SplitView>
  );
};

const Editor = ({ editor, fs }: EditorProps) => {
  const intl = useIntl();
  const connected = useConnectionStatus() === ConnectionStatus.CONNECTED;
  const [serialStateWhenOpen, setSerialStateWhenOpen] =
    useState<SizedMode>("compact");
  const serialSizedMode = connected ? serialStateWhenOpen : "collapsed";
  const [{ fontSize: settingsFontSizePt }] = useSettings();
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <Flex
      as="main"
      flex="1 1 100%"
      flexDirection="column"
      height="100%"
      boxShadow="4px 0px 24px #00000033"
    >
      <SplitView
        direction="column"
        minimums={[248, 200]}
        compactSize={SerialArea.compactSize}
        height="100%"
        mode={serialSizedMode}
      >
        <SplitViewRemainder>{editor}</SplitViewRemainder>
        <SplitViewDivider />
        <SplitViewSized>
          <SerialArea
            as="section"
            compact={serialSizedMode === "compact"}
            onSizeChange={setSerialStateWhenOpen}
            aria-label={intl.formatMessage({
              id: "serial-terminal",
            })}
            showSyncStatus={true}
            expandDirection="up"
            tabOutRef={ref.current!}
            terminalFontSizePt={settingsFontSizePt}
          />
        </SplitViewSized>
      </SplitView>
      <ProjectActionBar
        ref={ref}
        sendButtonRef={ref}
        as="section"
        aria-label={intl.formatMessage({ id: "project-actions" })}
        borderTopWidth={2}
        borderColor="gray.200"
        overflow="hidden"
        fs={fs}
      />
    </Flex>
  );
};

export default Workbench;
